import { useState } from "react";
import { Button } from "@/components/ui/button";
import { GraduationCap, Code2, Rocket, ArrowRight, X } from "lucide-react";
import elementaryImage from "@/assets/elementary-kids.webp";
import middleSchoolImage from "@/assets/middle-school-kids.webp";
import highSchoolImage from "@/assets/high-school-students.webp";
import { motion } from "framer-motion";
import * as Dialog from "@radix-ui/react-dialog";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};

export const LearningPathsSection = ({ studentLink, showEnrollNow = false }: { studentLink?: string, showEnrollNow?: boolean }) => {
  const paths = [
    {
      icon: <GraduationCap className="w-5 h-5" />,
      title: "Elementary School",
      subtitle: "Grades 3–5",
      description: "Foundational programming concepts through interactive exercises and guided exploration.",
      image: elementaryImage,
      buttonText: "Explore Elementary",
      curriculum: [
        "Remix a goofy Mad Libs story using print statements",
        "Build a tiny choose-your-own-adventure with input and if/else",
        "Create looping emoji/star patterns like digital pixel art",
        "Make custom mini-functions to personalize messages or designs",
        "Design a simple class (like a pet or toy) for a final mini project"
      ]
    },
    {
      icon: <Code2 className="w-5 h-5" />,
      title: "Middle School",
      subtitle: "Grades 6–8",
      description: "Structured projects and algorithmic thinking that builds core development competencies.",
      image: middleSchoolImage,
      buttonText: "Explore Middle School",
      curriculum: [
        "Build a mini interactive calculator using inputs and math",
        "Create a decision-based quiz or age checker with if/else logic",
        "Use loops and lists to generate patterns, countdowns, or item menus",
        "Write reusable functions to organize and simplify bigger programs",
        "Design a custom class (like a car or pet) for a small final project"
      ]
    },
    {
      icon: <Rocket className="w-5 h-5" />,
      title: "High School",
      subtitle: "Grades 9–12",
      description: "Advanced software engineering practices with portfolio development and career preparation.",
      image: highSchoolImage,
      buttonText: "Explore High School",
      curriculum: [
        "Build a command-line tool (like a tip or grade calculator) using variables and arithmetic",
        "Create a decision assistant that gives advice based on user inputs with if/elif/else logic",
        "Develop a study-time tracker that stores data in lists and analyzes totals and averages",
        "Implement a personal finance tracker using reusable functions to organize the codebase",
        "Design a habit-tracker app using classes, attributes, and methods for a capstone project"
      ]
    }
  ];

  return (
    <section className="relative pt-24 pb-12 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-5xl mx-auto text-center space-y-16"
        >
          {/* Header */}
          <motion.div variants={item} className="space-y-6">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground">
              Structured Learning <span className="text-primary">Pathways</span>
            </h2>
            <motion.div
              variants={item}
              className="h-0.5 bg-gradient-to-r from-primary/30 to-transparent w-24 mx-auto"
            />
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Progressive curriculum designed for mastery at every educational level.
            </p>
          </motion.div>

          {/* Learning Paths */}
          <div className="grid lg:grid-cols-3 gap-8">
            {paths.map((path) => (
              <motion.div
                key={path.title}
                variants={item}
                className="group bg-white/90 backdrop-blur-sm rounded-xl overflow-hidden border border-border shadow-card hover:shadow-hero transition-all flex flex-col"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={path.image}
                    alt={`${path.title} learning environment`}
                    width={800}
                    height={520}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex-1 space-y-4">
                    <div className="w-12 h-12 rounded-full bg-secondary/30 flex items-center justify-center text-foreground">
                      {path.icon}
                    </div>
                    <h3 className="text-xl font-medium text-foreground text-left">
                      {path.title}
                    </h3>
                    <p className="text-sm font-semibold text-primary uppercase tracking-wider text-left">
                      {path.subtitle}
                    </p>
                    <p className="text-muted-foreground leading-relaxed text-left">
                      {path.description}
                    </p>
                  </div>

                  <Dialog.Root>
                    <Dialog.Trigger asChild>
                      <Button
                        variant="accent"
                        size="lg"
                        className="mt-6 w-full font-medium"
                      >
                        {path.buttonText}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Dialog.Trigger>
                    <Dialog.Portal>
                      <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
                      <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto z-50 p-6 focus:outline-none">
                        <div className="relative">
                          <Dialog.Title className="text-2xl font-bold text-foreground mb-2">
                            {path.title} Curriculum
                          </Dialog.Title>
                          <p className="text-muted-foreground mb-6">{path.subtitle}</p>

                          <div className="space-y-4">
                            <h3 className="font-semibold text-lg">What You'll Learn:</h3>
                            <ul className="space-y-2">
                              {path.curriculum.map((item, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-primary mr-2">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="mt-8 flex justify-end space-x-4">
                            <Dialog.Close asChild>
                              <Button variant="outline">Close</Button>
                            </Dialog.Close>
                            {showEnrollNow && (
                              <Button variant="accent" onClick={() => window.open(studentLink || '#', '_blank')}>
                                Enroll Now
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </Button>
                            )}
                          </div>

                          <Dialog.Close asChild>
                            <button
                              className="absolute top-0 right-0 p-2 text-muted-foreground hover:text-foreground transition-colors"
                              aria-label="Close"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </Dialog.Close>
                        </div>
                      </Dialog.Content>
                    </Dialog.Portal>
                  </Dialog.Root>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};